import React, { useRef, useState } from 'react';
import { Form, Input, Button, Space, Spin, Alert } from 'antd';
import { EditOutlined, RobotOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { UPDATE_CAMPAIGN_MESSAGE, REGENERATE_CAMPAIGN_MESSAGE } from '../../graphql/mutations/campaignMessage';

const { TextArea } = Input;

const EditCampaignMessageForm = ({ message, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  
  // Set initial form values
  React.useEffect(() => {
    if (message) {
      form.setFieldsValue({
        message: message.message
      });
    }
  }, [message, form]);

  // Mutations
  const [updateCampaignMessage, { loading: updating }] = useMutation(UPDATE_CAMPAIGN_MESSAGE);
  const [regenerateCampaignMessage, { loading: regenerating }] = useMutation(REGENERATE_CAMPAIGN_MESSAGE);

  // Handle manual update
  const handleSubmit = async (values) => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      setError(null);
      const { data } = await updateCampaignMessage({
        variables: {
          id: message.id,
          message: values.message
        }
      });

      if (data.updateCampaignMessage.errors && data.updateCampaignMessage.errors.length > 0) {
        setError(data.updateCampaignMessage.errors.join(', '));
        return;
      }

      if (onSuccess) {
        onSuccess(data.updateCampaignMessage.campaignMessage);
      }
    } catch (err) {
      console.error('Error updating message:', err);
      setError('Erro ao atualizar mensagem: ' + err.message);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isActionBusy = updating || regenerating || isSubmitting;

  // Handle AI regeneration
  const handleRegenerate = async () => {
    try {
      setError(null);
      const { data } = await regenerateCampaignMessage({
        variables: {
          id: message.id
        }
      });

      if (data.regenerateCampaignMessage.errors && data.regenerateCampaignMessage.errors.length > 0) {
        setError(data.regenerateCampaignMessage.errors.join(', '));
        return;
      }

      // Update the form with the newly generated message
      form.setFieldsValue({
        message: data.regenerateCampaignMessage.campaignMessage.message
      });

      if (onSuccess) {
        onSuccess(data.regenerateCampaignMessage.campaignMessage);
      }
    } catch (err) {
      console.error('Error regenerating message:', err);
      setError('Erro ao regenerar mensagem: ' + err.message);
    }
  };

  return (
    <Spin spinning={updating || regenerating}>
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSubmit}
        initialValues={{
          message: message?.message || ''
        }}
      >
        {error && (
          <Alert 
            type="error" 
            message={error} 
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        <Form.Item
          name="message"
          label="Mensagem"
          rules={[{ required: true, message: 'Por favor, informe o texto da mensagem' }]}
        >
          <TextArea 
            rows={8} 
            placeholder="Digite o texto da mensagem"
            style={{ whiteSpace: 'pre-wrap' }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<EditOutlined />} 
              loading={updating || isSubmitting}
              disabled={isActionBusy}
            >
              Salvar Alterações
            </Button>
            <Button 
              type="default" 
              icon={<RobotOutlined />} 
              onClick={handleRegenerate}
              loading={regenerating}
              disabled={isActionBusy}
            >
              Regenerar com IA
            </Button>
            <Button onClick={onCancel} disabled={isActionBusy}>
              Cancelar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default EditCampaignMessageForm; 